import { createElement, useCallback, type CSSProperties } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import { FormControlLabel } from '@mui/material';
import { RadioV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import {
  ApprovalFormTable,
  ApprovalValueBox,
} from '@/components/Approval/@components/ApprovalFormTable';
import {
  BP_CYCLES,
  BP_REQUEST_TYPES,
  BP_SELECTION_METHOD_LABEL,
  BP_SOURCING_PATH_LABEL,
  bpAmountLabel,
  bpAmountSpec,
  bpInterestDetailLabels,
  bpRelationLabel,
  type BpFormState,
  type BpFormType,
} from '@/utils/approval/businessPartnerRegistration';
import { BpGuideText, BpInfoTable, BpViewRoot } from './styles';

export {
  BpCellStack,
  BpGuideText,
  BpIndentedField,
  BpInfoTable,
  BpRadioRow,
} from './styles';

export const BP_SELECTION_GUIDE = [
  [
    '경쟁입찰방식',
    '2개 이상의 업체를 비딩(평가)하여 최종 1개 업체를 선정한 경우',
  ],
  ['수의계약방식', '비딩 과정 없이 업체를 선정한 경우'],
] as const;

export const BP_INTEREST_GUIDE = [
  ['혈연', '가족 및 친인척 관계(본인 및 배우자 포함)'],
  ['학연', '학교 동문, 선후배 관계'],
  ['지연', '같은 지역 출신 지인 관계'],
  ['전직장 동료', '전 직장(근무이력이 있는 모든 직장) 동료 및 상/하급자 관계'],
  [
    '전직장 거래처',
    '전 직장(근무이력이 있는 모든 직장)과 거래관계가 있었던 거래처 및 거래처 임직원',
  ],
  [
    '자사 전현직 임직원',
    'F&F(관계사 포함)의 전 현직 임직원이 신규업체의 임직원 또는 관계자인 경우',
  ],
] as const;

/** 경쟁입찰이면 간략히, 수의계약이면 자세히 */
export const bpReasonLabel = (state: Pick<BpFormState, 'selectionMethod'>) =>
  state.selectionMethod === 'direct'
    ? '업체 선정 사유 자세히 기입'
    : '업체 선정 사유 간략히 기입';

export const BpSelectionGuide = ({ t }: { t: TFunction }) => (
  <>
    {BP_SELECTION_GUIDE.map(([title, desc], i) => (
      <span key={title}>
        {i > 0 && <br />}
        {t(title)} : {t(desc)}
      </span>
    ))}
  </>
);

export const BpInterestGuide = ({ t }: { t: TFunction }) => (
  <>
    [{t('이해관계 유형')}]
    {BP_INTEREST_GUIDE.map(([title, desc]) => (
      <span key={title}>
        <br />· {t(title)}: {t(desc)}
      </span>
    ))}
  </>
);

const hidden = { display: 'none' } as const;

type RadioProps = {
  name: string;
  value: string;
  label: string;
  checked: boolean;
  className?: string;
};

const MarkupRadio = ({
  name,
  value,
  label,
  checked,
  className,
}: RadioProps) => (
  <label className={className ? `radio ${className}` : 'radio'}>
    <input
      type="radio"
      name={name}
      value={value}
      disabled
      defaultChecked={checked}
    />
    <i />
    {label}
  </label>
);

const MarkupText = ({ name, value }: { name: string; value: string }) =>
  value ? (
    <div className="full-row ml-20">
      <input type="text" name={name} readOnly defaultValue={value} />
    </div>
  ) : null;

type MarkupProps = { state: BpFormState; apvType: BpFormType; t: TFunction };

/** 레거시 #eas_hk_bp_content·#eas_tw_bp_content 안쪽 마크업(id·class 동일) — 저장 시점 값을 담아 둠 */
const BpContentMarkup = ({ state, apvType, t }: MarkupProps) => {
  const isRegister = state.requestType === 'register';
  const { currency, options: amounts } = bpAmountSpec(apvType);
  const amountLabel = (a: (typeof amounts)[number]) =>
    bpAmountLabel(a, currency, t);
  const checkedRows = state.participants.filter((p) => p.checked);
  const detailLabels = bpInterestDetailLabels(apvType);
  return (
    <>
      <div id="selectedBpTypes" style={hidden}>
        {BP_REQUEST_TYPES.map((type) => (
          <MarkupRadio
            key={type.value}
            name="radioBpTypes"
            value={t(type.label)}
            label={t(type.label)}
            checked={state.requestType === type.value}
          />
        ))}
      </div>
      {isRegister && (
        <div id="selectedPartnerInfo" className="selected-partner-info">
          <table id="selectedPartnerInfoTable" className="info-table">
            <tbody>
              <tr>
                <th>{t('업체 선정 방식')}</th>
                <td>
                  <div className="guide">
                    <p className="guide-content guide-text">
                      <BpSelectionGuide t={t} />
                    </p>
                  </div>
                  <div className="d-flex flex-column">
                    <MarkupRadio
                      name="radioHow"
                      value={t(BP_SELECTION_METHOD_LABEL.bidding)}
                      label={t(BP_SELECTION_METHOD_LABEL.bidding)}
                      checked={state.selectionMethod === 'bidding'}
                    />
                    <MarkupText
                      name="selectionMethodWrite"
                      value={state.biddingVendors}
                    />
                  </div>
                  <MarkupRadio
                    name="radioHow"
                    className="mt-5"
                    value={t(BP_SELECTION_METHOD_LABEL.direct)}
                    label={t(BP_SELECTION_METHOD_LABEL.direct)}
                    checked={state.selectionMethod === 'direct'}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('업체 선정 사유')}</th>
                <td>
                  <label>
                    <span id="infoTextHow">{t(bpReasonLabel(state))}</span>
                  </label>
                  <div className="full-row">
                    <input
                      type="text"
                      name="selectionReason"
                      readOnly
                      defaultValue={state.selectionReason}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <th>{t('예상 거래 주기')}</th>
                <td>
                  <p className="guide-text">
                    {t(
                      '연간 예상 거래 주기 체크 (개별 프로젝트 단위로 횟수 카운팅)',
                    )}
                  </p>
                  <div className="d-flex flex-wrap">
                    {BP_CYCLES.map((c) => (
                      <MarkupRadio
                        key={c.value}
                        name="radioCycle"
                        value={t(c.label)}
                        label={t(c.label)}
                        checked={state.cycle === c.value}
                      />
                    ))}
                  </div>
                </td>
              </tr>
              <tr>
                <th>{t('예상 거래 금액 (연간)')}</th>
                <td>
                  <p className="guide-text">
                    {t(
                      '연간 예상 거래금액 체크 (1회 예상 거래금액 X 연간 예상 거래횟수)',
                    )}
                  </p>
                  <div className="d-flex flex-column">
                    {amounts.map((a) => (
                      <MarkupRadio
                        key={a.value}
                        name="radioAmount"
                        value={amountLabel(a)}
                        label={amountLabel(a)}
                        checked={state.amount === a.value}
                      />
                    ))}
                  </div>
                </td>
              </tr>
              <tr>
                <th>{t('업체 소싱 경로')}</th>
                <td>
                  <div className="d-flex flex-column">
                    <MarkupRadio
                      name="radioPath"
                      value={t(BP_SOURCING_PATH_LABEL.employee)}
                      label={t(BP_SOURCING_PATH_LABEL.employee)}
                      checked={state.sourcingPath === 'employee'}
                    />
                    <MarkupText
                      name="radioPath01Input"
                      value={state.sourcingEmployee}
                    />
                    <MarkupRadio
                      name="radioPath"
                      value={t(BP_SOURCING_PATH_LABEL.partner)}
                      label={t(BP_SOURCING_PATH_LABEL.partner)}
                      checked={state.sourcingPath === 'partner'}
                    />
                    <MarkupText
                      name="radioPath02Input"
                      value={state.sourcingPartner}
                    />
                    <MarkupRadio
                      name="radioPath"
                      value={t(BP_SOURCING_PATH_LABEL.internet)}
                      label={t(BP_SOURCING_PATH_LABEL.internet)}
                      checked={state.sourcingPath === 'internet'}
                    />
                    <MarkupRadio
                      name="radioPath"
                      value={t(BP_SOURCING_PATH_LABEL.other)}
                      label={t(BP_SOURCING_PATH_LABEL.other)}
                      checked={state.sourcingPath === 'other'}
                    />
                    <MarkupText
                      name="radioPath04Input"
                      value={state.sourcingOther}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <th>{t('업체 선정(평가) 과정 참여자')}</th>
                <td>
                  <p className="guide-text">
                    {t(
                      '해당 업체 선정 과정(업체 소싱, 평가, 계약조건 협의 등)에 참여한 모든 임직원 기재',
                    )}
                  </p>
                  {state.participants.length > 0 && (
                    <table id="table_participants">
                      <thead>
                        <tr>
                          <th>{t('소속')}</th>
                          <th>{t('성명')}</th>
                          <th>{t('직급')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {state.participants.map((p) => (
                          <tr
                            key={p.id}
                            className="employee"
                            data-userid={p.id}
                          >
                            <td className="text-center">{p.department}</td>
                            <td className="text-center">{p.name}</td>
                            <td className="text-center">{p.position}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </td>
              </tr>
              {state.participants.length > 0 && (
                <tr className="interests-flag">
                  <th>{t('이해관계 여부')}</th>
                  <td>
                    <div className="guide">
                      <p className="guide-content guide-text">
                        <BpInterestGuide t={t} />
                      </p>
                    </div>
                    <p className="guide-text">
                      {t(
                        '업체선정(평가) 과정에 참여한 임직원과 선정된 업체와의 이해관계 여부 체크',
                      )}
                    </p>
                    <div className="d-flex flex-wrap">
                      <MarkupRadio
                        name="radioInterests"
                        value="no"
                        label={t('이해관계 없음')}
                        checked={state.interest === 'no'}
                      />
                      <MarkupRadio
                        name="radioInterests"
                        value="yes"
                        label={t('이해관계 있음')}
                        checked={state.interest === 'yes'}
                      />
                    </div>
                  </td>
                </tr>
              )}
              {state.interest === 'yes' && checkedRows.length > 0 && (
                <tr className="interests-detail">
                  <th>
                    {t('이해관계 상세')}
                    {detailLabels.note && (
                      <>
                        <br />
                        <small>{t(detailLabels.note)}</small>
                      </>
                    )}
                  </th>
                  <td>
                    <p className="guide-text">
                      {t('이해관계가 있는 임직원 선택 후 이해관계 입력')}
                    </p>
                    <table id="table_interestDetails">
                      <thead>
                        <tr>
                          <th colSpan={3}>{t('임직원 정보')}</th>
                          <th colSpan={2}>{t('이해관계자 정보')}</th>
                          <th colSpan={2}>{t(detailLabels.contentHeader)}</th>
                        </tr>
                        <tr>
                          <th>{t('소속')}</th>
                          <th>{t('성명')}</th>
                          <th>{t('직급')}</th>
                          <th>{t('성명')}</th>
                          <th>{t('직함')}</th>
                          <th>{t('관계')}</th>
                          <th>{t('비고')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checkedRows.map((p) => (
                          <tr key={p.id}>
                            <td className="text-center">{p.department}</td>
                            <td className="text-center">{p.name}</td>
                            <td className="text-center">{p.position}</td>
                            <td className="text-center">{p.stakeholderName}</td>
                            <td className="text-center">
                              {p.stakeholderPosition}
                            </td>
                            <td className="text-center">
                              {bpRelationLabel(p.relation, t)}
                            </td>
                            <td className="text-center">{p.remark}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

/** attributes.content — 레거시 조회 화면은 이 HTML 을 그대로 출력 */
export const buildBpContentHtml = (
  state: BpFormState,
  apvType: BpFormType,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <BpContentMarkup state={state} apvType={apvType} t={t} />,
  );

/** 레거시 '변경' 문서는 업체소싱정보가 숨김 상태로 저장됨 */
export const hasBpPartnerInfo = (content: string): boolean => {
  const doc = new DOMParser().parseFromString(content, 'text/html');
  const info = doc.getElementById('selectedPartnerInfo');
  return (
    !!info && !/display\s*:\s*none/i.test(info.getAttribute('style') ?? '')
  );
};

const DROP_TAGS = new Set([
  'style',
  'script',
  'link',
  'i',
  'button',
  'colgroup',
]);

// 이해관계 상세: 성명은 영문 이름 한 줄, 직급·직함 한글 5자, 관계는 가장 긴 '자사 전현직 임직원' 폭 고정 — 남는 폭은 소속·비고
const WIDE_COL = 128;
const NARROW_COL = 84;
const RELATION_COL = 140;
const VIEW_COL_WIDTHS: Record<string, (number | string | undefined)[]> = {
  table_participants: ['50%', '25%', '25%'],
  table_interestDetails: [
    undefined,
    WIDE_COL,
    NARROW_COL,
    WIDE_COL,
    NARROW_COL,
    RELATION_COL,
    undefined,
  ],
};

// 모바일 최소 폭 = 고정 칸 합 + 소속 최소 140
const BP_DETAIL_VIEW_STYLE = {
  '--bp-fixed-width': `${WIDE_COL * 2 + NARROW_COL * 2 + RELATION_COL + 140}px`,
} as CSSProperties;

// 레거시 HTML 의 줄바꿈 공백이 표 안에 남으면 hydration 경고
const TABLE_PARTS = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr']);

const isHiddenElement = (element: Element): boolean =>
  element.id === 'selectedBpTypes' ||
  element.classList.contains('hide') ||
  element.classList.contains('hide-for-view') ||
  element.classList.contains('search-participants') ||
  /display\s*:\s*none/i.test(element.getAttribute('style') ?? '');

// 조회 전용이라 비활성이어도 본문색
const viewRadioSx = {
  margin: 0,
  gap: '8px',
  '& .MuiButtonBase-root': { padding: 0 },
  '& .MuiFormControlLabel-label.Mui-disabled': {
    color: 'var(--dk-text, #1A1A1A)',
    fontSize: 14,
    lineHeight: '20px',
  },
};

const selectedOptionText = (element: Element): string => {
  const options = Array.from(element.querySelectorAll('option'));
  const match = options.find((o) => o.hasAttribute('selected')) ?? options[0];
  return match?.textContent?.trim() ?? '';
};

type BpViewProps = {
  content: string;
  /** 기본 변환보다 먼저 — undefined 를 돌려주면 기본 변환으로 */
  transform?: ElementTransform;
};

/** 저장 content(레거시 문서 포함) 그대로 — 입력 요소만 조회용 공통 부품으로 */
export const BpView = ({ content, transform: override }: BpViewProps) => {
  const transform = useCallback<ElementTransform>(
    (ctx) => {
      const overridden = override?.(ctx);
      if (overridden !== undefined) return overridden;
      const { element, tagName, props, children } = ctx;
      if (DROP_TAGS.has(tagName)) return null;
      if (isHiddenElement(element)) return null;
      const key = props.key as string;
      if (tagName === 'input') {
        const type = element.getAttribute('type') ?? 'text';
        if (type !== 'text') return null;
        const value = element.getAttribute('value') ?? '';
        if (element.closest('td')?.closest('table:not(.info-table)')) {
          return value;
        }
        return value ? (
          <ApprovalValueBox key={key} filled>
            {value}
          </ApprovalValueBox>
        ) : null;
      }
      if (tagName === 'select') return selectedOptionText(element);
      if (tagName === 'label' && element.classList.contains('radio')) {
        const input = element.querySelector('input[type=radio]');
        return (
          <FormControlLabel
            key={key}
            className={element.className}
            sx={viewRadioSx}
            control={
              <RadioV2 disabled checked={!!input?.hasAttribute('checked')} />
            }
            label={element.textContent?.trim() ?? ''}
          />
        );
      }
      if (tagName === 'div' && element.classList.contains('guide')) {
        return (
          <ApprovalNoticeBox key={key} className="guide">
            {children}
          </ApprovalNoticeBox>
        );
      }
      if (tagName === 'p' && element.classList.contains('guide-text')) {
        if (element.classList.contains('guide-content')) {
          return <span key={key}>{children}</span>;
        }
        return <BpGuideText key={key}>{children}</BpGuideText>;
      }
      if (!TABLE_PARTS.has(tagName)) return undefined;
      const cells = children.filter(
        (child) => !(typeof child === 'string' && !child.trim()),
      );
      if (tagName === 'table') {
        if (element.classList.contains('info-table')) {
          return <BpInfoTable key={key}>{cells}</BpInfoTable>;
        }
        const widths = VIEW_COL_WIDTHS[element.id];
        const isDetail = element.id === 'table_interestDetails';
        return (
          <ApprovalFormTable
            key={key}
            className={
              element.id === 'table_participants'
                ? 'bp-fit'
                : isDetail
                ? 'bp-detail'
                : undefined
            }
            style={isDetail ? BP_DETAIL_VIEW_STYLE : undefined}
          >
            {widths && (
              <colgroup>
                {widths.map((width, i) => (
                  <col
                    key={i}
                    className={
                      isDetail && i === widths.length - 1
                        ? 'bp-remark'
                        : undefined
                    }
                    style={width ? { width } : undefined}
                  />
                ))}
              </colgroup>
            )}
            {cells}
          </ApprovalFormTable>
        );
      }
      return createElement(tagName, props, ...cells);
    },
    [override],
  );
  return <BpViewRoot>{useHtmlToReact(content, { transform })}</BpViewRoot>;
};
